import { CheckCircle2, Circle, Clock3, Loader2, MinusCircle, XCircle } from 'lucide-react';
import type { ProgressGroup } from '../api/ragBackend';

export function GroupedProgressList({ groups }: { groups: ProgressGroup[] }) {
  const runningIdx = groups.findIndex((g) => g.status === 'running');
  const nextIdx = groups.findIndex((g) => g.status === 'pending');
  const activeIdx = runningIdx >= 0 ? runningIdx : nextIdx;

  if (!groups.length) {
    return (
      <div className="overflow-hidden rounded-lg border border-sf-primary/15 bg-sf-surface shadow-sm">
        <div className="flex items-center gap-2 border-b border-sf-primary/10 bg-sf-primary-light/70 px-4 py-3">
          <Loader2 className="h-4 w-4 animate-spin text-sf-primary" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-sf-primary">
              Building reply
            </p>
            <p className="text-xs text-sf-gray">Preparing the first progress update...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-sf-primary/15 bg-sf-surface shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-sf-primary/10 bg-sf-primary-light/70 px-4 py-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-sf-primary">
            Building reply
          </p>
          <p className="text-xs text-sf-gray">Progress updates with elapsed time</p>
        </div>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sf-surface text-sf-primary ring-1 ring-sf-primary/15">
          <Clock3 className="h-4 w-4" />
        </div>
      </div>

      <ol className="divide-y divide-sf-primary/10">
        {groups.map((g, idx) => {
          const active = idx === activeIdx && g.status !== 'completed' && g.status !== 'failed';
          const done = g.status === 'completed';
          const failed = g.status === 'failed';
          const skipped = g.status === 'skipped';
          const time = done && g.durationMs != null ? `${(g.durationMs / 1000).toFixed(1)}s` : null;
          const Icon = done
            ? CheckCircle2
            : failed
              ? XCircle
              : skipped
                ? MinusCircle
                : active
                  ? Loader2
                  : Circle;
          const iconTone = done
            ? 'bg-sf-primary text-sf-secondary'
            : failed
              ? 'bg-red-50 text-red-600 ring-red-200'
              : skipped
                ? 'bg-sf-surface text-sf-gray/60 ring-sf-primary/15'
                : active
                  ? 'bg-sf-primary-light text-sf-primary ring-sf-primary/25'
                  : 'bg-sf-surface text-sf-gray/50 ring-sf-primary/15';
          const textTone =
            done || active ? 'text-sf-dark' : failed ? 'text-red-700' : 'text-sf-gray';

          return (
            <li
              key={g.key}
              className={`grid grid-cols-[2rem_1fr_auto] items-center gap-2 px-4 py-3 ${
                active ? 'bg-sf-primary-light/45' : ''
              }`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ring-1 ${iconTone}`}
              >
                <Icon className={`h-3.5 w-3.5 ${active ? 'animate-spin' : ''}`} />
              </span>
              <span className={`min-w-0 text-sm font-semibold leading-snug ${textTone}`}>
                {g.label}
              </span>
              {time ? (
                <span className="rounded-full bg-sf-primary-light px-2 py-0.5 text-[11px] font-semibold tabular-nums text-sf-primary">
                  {time}
                </span>
              ) : active ? (
                <span className="rounded-full bg-sf-surface px-2 py-0.5 text-[11px] font-semibold text-sf-primary ring-1 ring-sf-primary/15">
                  Now
                </span>
              ) : skipped ? (
                <span className="rounded-full bg-sf-surface px-2 py-0.5 text-[11px] font-semibold text-sf-gray ring-1 ring-sf-primary/15">
                  Skipped
                </span>
              ) : (
                <span className="text-[11px] font-medium text-sf-gray/50">
                  {String(idx + 1).padStart(2, '0')}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
